import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../utils/api/axios";
import SkillsSpiderChart from "../../components/researcher/SkillsSpiderChart";
import ContributionDonutChart from "../../components/researcher/ContributionDonutChart";

interface BusinessProfileData {
    profile: {
        name: string;
        avatarUrl: string | null;
        bio: string;
    };
    contribution: {
        totalFindings: number;
        platformContributionPercentage: number;
        timesAboveAverage: number;
        yourProgramsCount: number;
        yourProgramsPercentage: number;
        platformWidePercentage: number;
        acceptanceRate: number;
        fixRate: number;
        severityDistribution: {
            critical: number;
            high: number;
            medium: number;
            low: number;
        };
    };
    skills: Array<{ name: string; percentage: number }>;
    recentActivity: Array<{
        type: 'report' | 'retest';
        severity: string;
        category?: string;  // For reports
        status?: string;    // For retests
        programName: string;
        timeAgo: string;
    }>;
    totalActivityCount: number;
    certifications: string[];
    achievements: Array<{
        name: string;
        description: string;
    }>;
}

const BusinessResearcherProfile = () => {
    const { username } = useParams(); // Changed from userId to username
    const [profile, setProfile] = useState<BusinessProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activityPage, setActivityPage] = useState(0); // For pagination

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                // NOTE: baseURL already includes '/api/', so use path without leading slash
                const response = await axios.get(`v2/researchers/${username}/business-profile`);
                console.log('Business Profile Response:', response.data);
                console.log('Achievements:', response.data.achievements);
                console.log('Certifications:', response.data.certifications);
                setProfile(response.data);
            } catch (err: any) {
                setError(err.response?.data?.message || "Failed to load profile");
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            fetchProfile();
        }
    }, [username]);

    const getSeverityColor = (severity: string) => {
        const s = (severity || "").toLowerCase();
        if (s === "critical") return "bg-red-500";
        if (s === "high") return "bg-orange-500";
        if (s === "medium") return "bg-yellow-500";
        if (s === "low") return "bg-green-500";
        if (s === "info" || s === "informational" || s === "none") return "bg-blue-500";
        return "bg-gray-500";
    };

    const getSeverityIcon = (severity: string) => {
        const s = (severity || "").toLowerCase();
        if (s === "critical") return <div className="w-3 h-3 rounded-full bg-[#DC2626]"></div>;
        if (s === "high") return <div className="w-3 h-3 rounded-full bg-[#EA580C]"></div>;
        if (s === "medium") return <div className="w-3 h-3 rounded-full bg-[#CA8A04]"></div>;
        if (s === "low") return <div className="w-3 h-3 rounded-full bg-[#059669]"></div>;
        if (s === "info" || s === "informational" || s === "none") return <div className="w-3 h-3 rounded-full bg-[#3B82F6]"></div>;
        return <div className="w-3 h-3 rounded-full bg-gray-400"></div>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-gray-600">Loading profile...</div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-600">{error || "Profile not found"}</div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Researcher Profile</h1>
                    <p className="text-gray-600 mt-2">Security researcher information and performance metrics</p>
                </div>

                {/* Profile Header Card */}
                <div className="bg-white rounded-xl shadow-sm p-8 mb-6 border-2 border-[#E5E7EB]">
                    <div className="flex items-start gap-6">
                        {/* Avatar with online indicator */}
                        <div className="relative">
                            <img
                                src={profile.profile.avatarUrl || "/favicon.ico"}
                                alt={profile.profile.name}
                                className="w-20 h-20 rounded-full object-cover bg-blue-100"
                            />
                            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>

                        {/* Profile Info */}
                        <div className="flex-1">
                            <h2 className="text-xl font-[500] text-[#010D2C]">{profile.profile.name}</h2>
                            <div className="inline-block mt-2 text-[#58CC02] text-sm font-normal rounded">
                                Security Researcher
                            </div>
                            {profile.profile.bio && (
                                <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-3xl">{profile.profile.bio}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Flex Layout */}
                <div className="flex flex-col lg:flex-row gap-6 mb-6">
                    {/* Left Column - Platform Contribution (Wider) */}
                    <div className="lg:flex-[1.9]">
                        <div className="bg-white rounded-xl shadow-sm p-6 h-full border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#010D2C] mb-6 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#E8EFFE] rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#4169E1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                Platform Contribution
                            </h3>
                            <ContributionDonutChart
                                percentage={profile.contribution.platformContributionPercentage}
                                totalFindings={profile.contribution.totalFindings}
                                timesAboveAverage={profile.contribution.timesAboveAverage}
                            />
                        </div>
                    </div>

                    {/* Right Column - Quality Metrics & Findings (Narrower) */}
                    <div className="lg:flex-[1.2] flex flex-col gap-6">
                        {/* Quality Metrics */}
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-4 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#E8FFF3] rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#58CC02]" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                Quality Metrics
                                <div className="group relative">
                                    <svg className="w-4 h-4 text-[#9CA3AF] hover:text-[#1E3A8A] cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-[#1F2937] text-white text-xs rounded-lg shadow-lg z-10">
                                        <div className="mb-2">
                                            <span className="font-semibold">Acceptance Rate:</span> Percentage of submitted reports approved across the platform
                                        </div>
                                        <div>
                                            <span className="font-semibold">Fix Rate:</span> Percentage of verified fixes after retesting across all programs
                                        </div>
                                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#1F2937]"></div>
                                    </div>
                                </div>
                            </h3>
                            <div className="flex gap-3">
                                {/* Acceptance Rate */}
                                <div className="flex-1 bg-[#58CC0214] rounded-lg p-4 text-center border-[0.5px] border-[#58CC02]">
                                    <div className="text-3xl font-bold text-[#010D2C] mb-1">
                                        {profile.contribution.acceptanceRate}%
                                    </div>
                                    <div className="text-xs font-[500] text-[#010D2C]">Acceptance</div>
                                </div>

                                {/* Fix Rate */}
                                <div className="flex-1 bg-[#58CC0214] rounded-lg p-4 text-center border-[0.5px] border-[#58CC02]">
                                    <div className="text-3xl font-bold text-[#010D2C] mb-1">
                                        {profile.contribution.fixRate}%
                                    </div>
                                    <div className="text-xs font-[500] text-[#010D2C]">Fix Rate</div>
                                </div>
                            </div>
                        </div>

                        {/* Findings Distribution */}
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-4 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                Findings Distribution across your programs
                            </h3>
                            <div className="flex flex-wrap gap-3">
                                {Object.entries(profile.contribution.severityDistribution).map(([severity, percentage]) => {
                                    const count = Math.round((percentage / 100) * (profile.contribution.yourProgramsCount || 0));
                                    const getSeverityStyle = (sev: string) => {
                                        switch (sev.toLowerCase()) {
                                            case 'critical': return { bg: 'bg-white', border: 'border-[0.5px] border-[#FCA5A5]', text: 'text-[#DC2626]' };
                                            case 'high': return { bg: 'bg-white', border: 'border-[0.5px] border-[#FDBA74]', text: 'text-[#EA580C]' };
                                            case 'medium': return { bg: 'bg-white', border: 'border border-[#FDE047]', text: 'text-[#CA8A04]' };
                                            case 'low': return { bg: 'bg-white', border: 'border border-[#6EE7B7]', text: 'text-[#059669]' };
                                            default: return { bg: 'bg-white', border: 'border-[0.5px] border-gray-200', text: 'text-gray-600' };
                                        }
                                    };
                                    const style = getSeverityStyle(severity);
                                    return (
                                        <div key={severity} className={`flex-1 min-w-[calc(50%-0.375rem)] ${style.bg} rounded-lg py-6 px-4 text-center ${style.border}`}>
                                            <div className={`text-3xl font-bold ${style.text} mb-1`}>{count}</div>
                                            <div className="text-xs font-[500] text-[#010D2C] capitalize">{severity}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-4 text-center py-2 bg-white rounded-lg border-[1.5px] border-[#BFDBFE]">
                                <span className="text-xs text-[#6B7280] font-[500]">Total Findings: </span>
                                <span className="text-lg font-bold text-[#010D2C]">{profile.contribution.yourProgramsCount || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section - Skills & Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Skills Spider Chart */}
                    {profile.skills?.some(s => s.percentage > 0) && (
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-6 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#F0FDF4] rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                Expertise Areas
                            </h3>
                            <SkillsSpiderChart skills={profile.skills || []} />
                        </div>
                    )}

                    {/* Recent Activity */}
                    {profile.recentActivity?.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-6 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-[#FEF9C3] rounded-lg flex items-center justify-center">
                                        <svg className="w-5 h-5 text-[#CA8A04]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    Recent Activity
                                </div>
                                <span className="text-sm text-[#6B7280] font-normal">
                                    {Math.min(activityPage * 3 + 3, profile.recentActivity?.length || 0)} of {profile.recentActivity?.length || 0}
                                </span>
                            </h3>
                            <div className="space-y-3">
                                {profile.recentActivity?.slice(activityPage * 3, (activityPage + 1) * 3).map((activity, index) => (
                                    <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-[#E5E7EB] hover:shadow-sm transition-shadow">
                                        <div className="flex items-center justify-center w-6 h-6">{getSeverityIcon(activity.severity)}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                {/* Type Badge */}
                                                <span className={`px-2 py-0.5 rounded text-xs font-[500] flex items-center gap-1 ${activity.type === 'report'
                                                    ? 'bg-[#DBEAFE] text-[#1E40AF]'
                                                    : 'bg-[#E9D5FF] text-[#7C3AED]'
                                                    }`}>
                                                    {activity.type === 'report' ? (
                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                    {activity.type === 'report' ? 'Report' : 'Retest'}
                                                </span>
                                                <span className="font-bold text-[#010D2C] text-sm">{activity.severity}</span>
                                                {activity.type === 'report' && activity.category && (
                                                    <>
                                                        <span className="text-[#9CA3AF]">•</span>
                                                        <span className="text-sm text-[#6B7280] font-normal">{activity.category}</span>
                                                    </>
                                                )}
                                                {activity.type === 'retest' && activity.status && (
                                                    <>
                                                        <span className="text-[#9CA3AF]">•</span>
                                                        <span className="text-sm text-[#6B7280] font-normal">{activity.status}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="text-sm text-[#374151] font-normal">{activity.programName}</div>
                                            <div className="text-xs text-[#9CA3AF] mt-0.5">{activity.timeAgo}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {/* Clickable Pagination */}
                            {profile.recentActivity?.length > 3 && (
                                <div className="mt-4 flex items-center justify-center gap-3">
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => setActivityPage(Math.max(0, activityPage - 1))}
                                        disabled={activityPage === 0}
                                        className={`p-1 rounded ${activityPage === 0
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-orange-600 hover:bg-orange-50 cursor-pointer'
                                            }`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>

                                    {/* Page Dots */}
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.ceil((profile.recentActivity?.length || 0) / 3) }).map((_, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setActivityPage(idx)}
                                                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${idx === activityPage
                                                    ? 'bg-orange-600 w-6'
                                                    : 'bg-gray-300 hover:bg-gray-400'
                                                    }`}
                                            />
                                        ))}
                                    </div>

                                    {/* Next Button */}
                                    <button
                                        onClick={() => setActivityPage(Math.min(Math.ceil((profile.recentActivity?.length || 0) / 3) - 1, activityPage + 1))}
                                        disabled={activityPage >= Math.ceil((profile.recentActivity?.length || 0) / 3) - 1}
                                        className={`p-1 rounded ${activityPage >= Math.ceil((profile.recentActivity?.length || 0) / 3) - 1
                                            ? 'text-gray-300 cursor-not-allowed'
                                            : 'text-orange-600 hover:bg-orange-50 cursor-pointer'
                                            }`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Certifications & Achievements Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Certifications */}
                    {profile.certifications?.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-6 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#E8FFF3] rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#58CC02]" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                Certifications
                            </h3>
                            <div className="space-y-3">
                                {profile.certifications?.map((cert, index) => (
                                    <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-[#E5E7EB]">
                                        <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center flex-shrink-0">
                                            <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-[500] text-[#010D2C]">{cert}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Achievements */}
                    {profile.achievements?.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-[#E5E7EB]">
                            <h3 className="text-base font-[500] text-[#1E3A8A] mb-6 flex items-center gap-2">
                                <div className="w-8 h-8 bg-[#FEF9C3] rounded-lg flex items-center justify-center">
                                    <img src="/icons/ach-prof.svg" alt="Achievements" className="w-5 h-5" />
                                </div>
                                Achievements
                            </h3>
                            <div className="space-y-3">
                                {profile.achievements?.map((achievement, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className="flex items-start gap-3 p-4 rounded-lg border border-[#FDE68A]"
                                            style={{ background: 'linear-gradient(to right, #FFFBEB, #FEFCE8)' }}
                                        >
                                            <div className="flex-shrink-0">
                                                <div className="w-10 h-10 bg-[#FCD34D] rounded-full flex items-center justify-center">
                                                    <img src="/icons/star-res.svg" alt="Achievement" className="w-5 h-5" />
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-bold text-[#010D2C] text-sm mb-1">{achievement.name}</h4>
                                                <p className="text-xs text-[#6B7280]">{achievement.description}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BusinessResearcherProfile;
